import type { ReactNode } from 'react';
import {
  BarChart3,
  Banknote,
  Bot,
  Building2,
  ChevronDown,
  CircleDollarSign,
  GitCompareArrows,
  ListChecks,
  LogOut,
  Menu,
  ReceiptText,
  RefreshCw,
  RotateCcw,
  Save,
  X,
} from './icons';
import { formatDateTime } from '../domain/format';
import type { FinanceData } from '../domain/types';
import type { IntegrationStatus } from '../data/financeAdapter';
import { useScenario } from '../state/ScenarioContext';

export type ViewId = 'executive' | 'agent' | 'expenses' | 'assumptions' | 'revenue' | 'cash' | 'compare';

export interface NavItem {
  id: ViewId;
  label: string;
  icon: typeof BarChart3;
}

export const navItems: NavItem[] = [
  { id: 'executive', label: 'Executive', icon: BarChart3 },
  { id: 'agent', label: 'Ask finance AI', icon: Bot },
  { id: 'expenses', label: 'Expense explorer', icon: ReceiptText },
  { id: 'assumptions', label: 'Assumption register', icon: ListChecks },
  { id: 'revenue', label: 'Revenue', icon: CircleDollarSign },
  { id: 'cash', label: 'Cash, AR & debt', icon: Banknote },
  { id: 'compare', label: 'Scenario compare', icon: GitCompareArrows },
];

interface AppShellProps {
  data: FinanceData;
  mode: 'authenticated' | 'demo';
  integration: IntegrationStatus;
  activeView: ViewId;
  onViewChange: (view: ViewId) => void;
  mobileNavOpen: boolean;
  onMobileNavToggle: () => void;
  onQuickBooksRefresh: () => void;
  onLogout: () => void;
  refreshPending: boolean;
  refreshError: string | null;
  modelRefreshNotice: string | null;
  children: ReactNode;
}

export const AppShell = ({
  data,
  mode,
  integration,
  activeView,
  onViewChange,
  mobileNavOpen,
  onMobileNavToggle,
  onQuickBooksRefresh,
  onLogout,
  refreshPending,
  refreshError,
  modelRefreshNotice,
  children,
}: AppShellProps) => {
  const { inputs, result, updateInputs, resetScenario, saveScenario } = useScenario();
  const active = navItems.find((item) => item.id === activeView) ?? navItems[0];
  const qbo = integration.quickBooks;
  const qaSandbox = integration.access?.qaSandbox === true;
  const connectUrl = qbo.connectUrl?.startsWith('/') ? qbo.connectUrl : '/api/qbo/connect';

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileNavOpen ? 'is-open' : ''}`}>
        <div className="brand">
          <div className="brand-mark" aria-hidden="true"><Building2 size={20} /></div>
          <div>
            <strong>deverus</strong>
            <span>Finance intelligence</span>
          </div>
          <button className="icon-button sidebar-close" onClick={onMobileNavToggle} aria-label="Close navigation">
            <X size={18} />
          </button>
        </div>
        <nav className="primary-nav" aria-label="Primary navigation">
          <p className="nav-label">Decision center</p>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                className={activeView === item.id ? 'nav-item active' : 'nav-item'}
                onClick={() => {
                  onViewChange(item.id);
                  if (mobileNavOpen) onMobileNavToggle();
                }}
                aria-current={activeView === item.id ? 'page' : undefined}
              >
                <Icon size={18} aria-hidden="true" />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <span className={`status-dot ${mode === 'demo' ? 'demo' : ''}`} />
          <div>
            <strong>{qaSandbox ? 'Synthetic QA fixture' : mode === 'demo' ? 'Demo data' : data.dataStatus === 'live' ? 'Live QBO P&L + forecast' : 'Workbook + QBO snapshot'}</strong>
            <span>{formatDateTime(data.lastSyncedAt)}</span>
          </div>
          <button className="icon-button" onClick={onLogout} aria-label="Sign out" title="Sign out">
            <LogOut size={16} />
          </button>
        </div>
      </aside>
      {mobileNavOpen ? <button className="nav-scrim" onClick={onMobileNavToggle} aria-label="Close navigation" /> : null}

      <main className="main-content">
        <header className="topbar">
          <div className="topbar-title">
            <button className="icon-button mobile-menu" onClick={onMobileNavToggle} aria-label="Open navigation">
              <Menu size={20} />
            </button>
            <div>
              <p className="eyebrow">{data.company}</p>
              <h1>{active.label}</h1>
            </div>
          </div>
          <div className="scenario-actions">
            <label className="scenario-name">
              <span className="sr-only">Scenario name</span>
              <input
                value={inputs.name}
                onChange={(event) => updateInputs({ name: event.target.value })}
                aria-label="Scenario name"
              />
              <ChevronDown size={14} aria-hidden="true" />
            </label>
            <button className="button ghost" onClick={resetScenario}>
              <RotateCcw size={16} aria-hidden="true" />
              Reset
            </button>
            <button className="button primary" onClick={saveScenario}>
              <Save size={16} aria-hidden="true" />
              Save scenario
            </button>
          </div>
        </header>
        {mode === 'demo' || qaSandbox ? (
          <div className="demo-banner" role="status">
            <strong>{qaSandbox ? 'QA sandbox.' : 'Demo mode.'}</strong> Values are synthetic and cannot be used for company decisions. Production financial data, named payroll, QuickBooks controls, and mutations are unavailable in this session.
          </div>
        ) : null}
        {mode === 'authenticated' && !qaSandbox ? (
          <div className={`integration-banner ${qbo.connected ? 'connected' : qbo.configured ? 'disconnected' : 'unconfigured'}`} role="status">
            <span className="integration-icon"><RefreshCw size={15} /></span>
            <div>
              <strong>
                {!qbo.configured
                  ? 'QuickBooks setup required'
                  : qbo.connected
                    ? `QuickBooks connected${qbo.companyName ? ` · ${qbo.companyName}` : ''}`
                    : 'QuickBooks is not connected'}
              </strong>
              <span>
                {!qbo.configured
                  ? 'Add the Intuit client ID and secret to the secure server environment.'
                  : qbo.connected
                    ? qbo.lastSyncedAt
                      ? `P&L, customer sales, cash, AR, and debt refreshed ${formatDateTime(qbo.lastSyncedAt)} through ${data.sourceFreshness?.cashArDebtAsOf ?? 'the latest closed month'}.`
                      : 'Connected; first refresh is pending.'
                    : 'Connect to refresh P&L, customer sales, cash, AR, and debt through the latest closed month.'}
              </span>
              {refreshError ? <span className="integration-error">{refreshError}</span> : null}
            </div>
            {qbo.configured && !qbo.connected ? (
              <a className="button primary compact" href={connectUrl}>Connect QuickBooks</a>
            ) : null}
            {qbo.connected ? (
              <button className="button compact" onClick={onQuickBooksRefresh} disabled={refreshPending}>
                <RefreshCw className={refreshPending ? 'is-spinning' : ''} size={14} />
                {refreshPending ? 'Refreshing…' : 'Refresh all QBO actuals'}
              </button>
            ) : null}
          </div>
        ) : null}
        {mode === 'authenticated' && !qaSandbox && !integration.workbook.storage.persistent ? (
          <div className="decision-readiness-banner" role="alert">
            <strong>Durable storage required for decision-grade operation.</strong>
            <span>QuickBooks data will recover automatically from this connected browser after a restart, but uploaded workbook history is not protected until a persistent disk or database is configured.</span>
          </div>
        ) : null}
        {mode === 'authenticated' && integration.decisionReadiness?.status === 'blocked' ? (
          <div className="decision-readiness-banner" role="alert">
            <strong>Financial model needs reconciliation before decision use.</strong>
            <span>{integration.decisionReadiness.blockingReasons.join(' ')}</span>
          </div>
        ) : null}
        {modelRefreshNotice ? (
          <div className="decision-readiness-banner model-refresh-banner" role="status">
            <strong>Latest financial model loaded.</strong>
            <span>{modelRefreshNotice}</span>
          </div>
        ) : null}
        {result.conflictsResolved?.length ? (
          <div className="decision-readiness-banner" role="status">
            <strong>Overlapping scenario controls were resolved.</strong>
            <span>{result.conflictsResolved.join(' ')}</span>
          </div>
        ) : null}
        <div className="page-content">{children}</div>
      </main>
    </div>
  );
};
